import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "../api";
import type { DocStatus, DocumentSummary } from "../api/types";
import { ApiError } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { FolderNav } from "../components/FolderNav";
import { StatusBadge } from "../components/StatusBadge";
import { UploadDialog } from "../components/UploadDialog";
import { ConfirmDialog } from "../components/shared/ConfirmDialog";
import { Button } from "../compose/controls";
import {
  SegmentedControl,
  type SegmentedOption,
} from "@norkron/web-segmented-control";
import { Inbox, Plus, RotateCcw, ShieldCheck, Trash2 } from "../compose/Icon";
import { EmptyState, ErrorState, SkeletonRows } from "../compose/states";
import { useToast } from "../compose/Toast";
import { formatDate } from "../lib/format";
import { useAsync } from "../lib/useAsync";

/** The library view filter. `archived` is a STAFF-ONLY pseudo-status: it doesn't
 *  filter the live list — it switches to the soft-deleted documents so staff can
 *  restore one (the delete dialog promises "Staff can restore it"). */
type LibraryFilter = DocStatus | "all" | "archived";

const STATUS_SEGMENTS: SegmentedOption<LibraryFilter>[] = [
  { value: "all", label: "All" },
  { value: "delivered", label: "Delivered" },
  { value: "draft", label: "Draft" },
  { value: "superseded", label: "Superseded" },
];

const ARCHIVED_SEGMENT: SegmentedOption<LibraryFilter> = {
  value: "archived",
  label: "Archived",
};

/**
 * Workspace library `/w/:slug` (design screens §8). Folder tree + a document
 * list with load-bearing status badges, filterable by status + folder (both
 * URL-synced so a view is shareable). Workspace accent theming is applied by
 * the shell around this region.
 */
export function Workspace() {
  const { slug = "" } = useParams<{ slug: string }>();
  const [params, setParams] = useSearchParams();
  const status = (params.get("status") as LibraryFilter) ?? "all";
  const folder = params.get("folder");
  const archivedView = status === "archived";

  // Folder management + upload + delete/restore are staff-only, mirroring the
  // server gate exactly (global staff OR a workspace `staff`-role member). Non-staff
  // never see the affordances, so the tree + status filter render unchanged for
  // readers, and the staff-only "Archived" filter is hidden from them.
  const { user, memberships } = useAuth();
  const canManage =
    !!user?.is_staff ||
    memberships.some((m) => m.workspace_slug === slug && m.role === "staff");

  const ws = useAsync(() => api.getWorkspace(slug), [slug]);
  const docs = useAsync(
    () =>
      archivedView
        ? api.listArchivedDocuments(slug)
        : api.listDocuments(slug, {
            folder: folder ?? undefined,
            status: status === "all" ? undefined : status,
          }),
    [slug, folder, status, archivedView],
  );

  const setStatus = (next: LibraryFilter) => {
    const p = new URLSearchParams(params);
    if (next === "all") p.delete("status");
    else p.set("status", next);
    setParams(p, { replace: true });
  };
  const setFolder = (next: string | null) => {
    const p = new URLSearchParams(params);
    if (next === null) p.delete("folder");
    else p.set("folder", next);
    setParams(p, { replace: true });
  };

  const toast = useToast();
  const [uploadOpen, setUploadOpen] = useState(false);
  // Document delete/archive (staff-only, mirrors the server gate). `pendingDelete`
  // is the row awaiting confirmation; the archive drops it from the list + the
  // sidebar counts (both reloaded on success).
  const [pendingDelete, setPendingDelete] = useState<DocumentSummary | null>(null);
  const [deleting, setDeleting] = useState(false);
  // Restore is NON-destructive (un-hide a soft-deleted doc) — no confirm dialog,
  // just a per-row action + toast. `restoringId` disables the row while in flight.
  const [restoringId, setRestoringId] = useState<string | null>(null);

  const archiveDoc = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await api.archiveDocument(pendingDelete.id);
      toast.show(`“${pendingDelete.title}” deleted`);
      setPendingDelete(null);
      docs.reload();
      ws.reload();
    } catch (err) {
      toast.show(
        err instanceof ApiError
          ? err.message || "Couldn't delete the document."
          : "Couldn't delete the document.",
      );
    } finally {
      setDeleting(false);
    }
  };

  const restoreDoc = async (doc: DocumentSummary) => {
    setRestoringId(doc.id);
    try {
      await api.restoreDocument(doc.id);
      toast.show(`“${doc.title}” restored`);
      docs.reload(); // drops it from the Archived list
      ws.reload(); // live counts tick back up
    } catch (err) {
      toast.show(
        err instanceof ApiError
          ? err.message || "Couldn't restore the document."
          : "Couldn't restore the document.",
      );
    } finally {
      setRestoringId(null);
    }
  };

  const createFolder = async (name: string) => {
    await api.createFolder(slug, name);
    toast.show(`Folder “${name}” created`);
    ws.reload();
  };
  const renameFolder = async (id: string, name: string) => {
    const previous = ws.data?.folders.find((f) => f.id === id)?.name;
    await api.renameFolder(id, name);
    toast.show("Folder renamed");
    if (previous && folder === previous) setFolder(name);
    ws.reload();
  };

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1 className="page__title">{ws.data?.name ?? "Library"}</h1>
          <p className="page__sub">
            {ws.data?.client ?? ""} · the content and its review state
          </p>
        </div>
        <Button
          variant="primary"
          data-testid="upload-document"
          disabled={!ws.data}
          icon={<Plus size={15} strokeWidth={1.5} />}
          onClick={() => setUploadOpen(true)}
        >
          Upload
        </Button>
      </div>

      <div className="library">
        <aside className="library__aside">
          <h2 className="library__aside-h">Folders</h2>
          {ws.data ? (
            <FolderNav
              folders={ws.data.folders}
              active={folder}
              onSelect={setFolder}
              total={ws.data.total_documents}
              canManage={canManage}
              onCreate={canManage ? createFolder : undefined}
              onRename={canManage ? renameFolder : undefined}
            />
          ) : (
            <SkeletonRows rows={3} />
          )}
        </aside>

        <section>
          <div className="docbar">
            <SegmentedControl
              ariaLabel="Filter by status"
              value={status}
              // The staff-only "Archived" pill reaches the soft-deleted documents so
              // staff can restore one; readers never see it.
              options={
                canManage
                  ? [...STATUS_SEGMENTS, ARCHIVED_SEGMENT]
                  : STATUS_SEGMENTS
              }
              segmentTestIdPrefix="filter"
              onChange={setStatus}
            />
            <span className="page__sub" style={{ margin: 0 }}>
              {docs.data ? `${docs.data.length} document${docs.data.length === 1 ? "" : "s"}` : ""}
            </span>
          </div>

          {docs.loading ? (
            <div className="doclist">
              <SkeletonRows rows={4} />
            </div>
          ) : docs.error ? (
            <ErrorState body="We couldn't load documents for this workspace." />
          ) : docs.data && docs.data.length > 0 ? (
            <div
              className="doclist"
              data-testid={archivedView ? "archived-list" : "doc-list"}
            >
              {docs.data.map((d) => (
                <DocRow
                  key={d.id}
                  slug={slug}
                  doc={d}
                  canManage={canManage}
                  archived={archivedView}
                  onDelete={() => setPendingDelete(d)}
                  onRestore={() => restoreDoc(d)}
                  restoring={restoringId === d.id}
                />
              ))}
            </div>
          ) : (
            <div className="doclist">
              <EmptyState
                icon={<Inbox size={30} strokeWidth={1.5} />}
                title={archivedView ? "No archived documents" : "Nothing here yet"}
                body={
                  archivedView
                    ? "Deleted documents show up here so staff can restore them. Nothing has been deleted."
                    : "No documents match this filter. Clear it, or upload a briefing to start a new review."
                }
              />
            </div>
          )}
        </section>
      </div>

      {uploadOpen && ws.data ? (
        <UploadDialog
          slug={slug}
          folders={ws.data.folders}
          onClose={() => setUploadOpen(false)}
          onUploaded={() => {
            docs.reload();
            ws.reload();
          }}
        />
      ) : null}

      {pendingDelete ? (
        <ConfirmDialog
          title="Delete document"
          body={`“${pendingDelete.title}” will be removed from the library. Staff can restore it.`}
          confirmLabel="Delete document"
          danger
          busy={deleting}
          onConfirm={archiveDoc}
          onCancel={() => (deleting ? undefined : setPendingDelete(null))}
          testid="delete-doc-dialog"
        />
      ) : null}
    </div>
  );
}

function DocRow({
  slug,
  doc,
  canManage,
  archived = false,
  onDelete,
  onRestore,
  restoring = false,
}: {
  slug: string;
  doc: DocumentSummary;
  canManage: boolean;
  archived?: boolean;
  onDelete: () => void;
  onRestore: () => void;
  restoring?: boolean;
}) {
  const body = (
    <>
      <div className="docrow__main">
        <div
          className={`docrow__title${
            doc.status === "superseded" ? " docrow__title--superseded" : ""
          }`}
        >
          {doc.title}
        </div>
        <div className="docrow__meta">
          <span>{doc.category}</span>
          <span className="dot">·</span>
          <span>{doc.brand}</span>
          <span className="dot">·</span>
          <span>{doc.lang.toUpperCase()}</span>
          <span className="dot">·</span>
          <span>{doc.format}</span>
          {doc.delivered_at ? (
            <>
              <span className="dot">·</span>
              <span>Delivered {formatDate(doc.delivered_at)}</span>
            </>
          ) : null}
        </div>
      </div>
      <div className="docrow__right">
        {doc.has_evidence ? (
          <span className="evidence-tick" title="Delivery evidence available">
            <ShieldCheck size={14} strokeWidth={1.5} />
            Evidence
          </span>
        ) : null}
        <StatusBadge status={doc.status} />
      </div>
    </>
  );

  return (
    <div className="docrow-wrap">
      {archived ? (
        // An archived doc 404s from the reader, so its row is NOT a link — it's a
        // static entry whose only action is Restore.
        <div className="docrow docrow--archived" data-testid={`doc-row-${doc.id}`}>
          {body}
        </div>
      ) : (
        <Link
          to={`/w/${slug}/doc/${doc.id}`}
          className="docrow"
          data-testid={`doc-row-${doc.id}`}
        >
          {body}
        </Link>
      )}
      {canManage && archived ? (
        <button
          type="button"
          className="docrow__restore"
          aria-label={`Restore ${doc.title}`}
          title="Restore document"
          data-testid={`doc-restore-${doc.id}`}
          disabled={restoring}
          onClick={onRestore}
        >
          <RotateCcw size={16} strokeWidth={1.5} />
        </button>
      ) : canManage ? (
        <button
          type="button"
          className="docrow__delete"
          aria-label={`Delete ${doc.title}`}
          title="Delete document"
          data-testid={`doc-delete-${doc.id}`}
          onClick={onDelete}
        >
          <Trash2 size={16} strokeWidth={1.5} />
        </button>
      ) : null}
    </div>
  );
}
